/** Limite do rótulo. É o da regra: `lote.size() <= 40` (`firestore/firestore.rules:107` e `:205`). */
export const MAX_LOTE = 40;
/**
 * Normaliza o que a pessoa digitou.
 *
 * Espaços nas pontas e repetidos somem: "LOTE  12 " e "LOTE 12" são o mesmo lote, e deixar
 * os dois entrarem quebraria justamente a busca que o lote existe para permitir.
 */
export function normalizarLote(bruto) {
    return bruto.trim().replace(/\s+/g, ' ').slice(0, MAX_LOTE);
}
export function loteValido(bruto) {
    return normalizarLote(bruto).length > 0;
}
/**
 * O estoque tem lote aberto agora?
 *
 * Exige o rótulo **e** o carimbo do ciclo corrente. Sem o carimbo, um lote de meses atrás
 * continuaria valendo depois de finalizar, e a contagem nova entraria no nome da antiga.
 */
export function loteAtivo(estoque) {
    if (!estoque)
        return false;
    const rotulo = (estoque.lote ?? '').trim();
    if (rotulo === '')
        return false;
    return estoque.loteCiclo === (estoque.contagemCycle ?? 1);
}
/** O lote em vigor, ou `null` quando não há. */
export function loteDe(estoque) {
    return loteAtivo(estoque) ? ((estoque?.lote ?? '').trim() || null) : null;
}
/**
 * Como a rodada aparece na tela: `Lote X` com lote ativo, senão `Sem lote`.
 *
 * O `contagemCycle` é contador interno e não vai à tela. Vale o carimbo, não a presença do
 * texto: o lote vencido continua no documento depois de finalizar.
 */
export function rotuloDaRodada(estoque) {
    const lote = loteDe(estoque);
    return lote ? `Lote ${lote}` : 'Sem lote';
}
/** Mesma regra para uma auditoria já salva, que guarda o lote junto. O contador interno não vai à tela. */
export function rotuloDaAuditoria(auditoria) {
    const lote = (auditoria.lote ?? '').trim();
    return lote ? `Lote ${lote}` : 'Sem lote';
}
/** Limite do `nome` da auditoria na regra (`firestore/firestore.rules:248`). */
export const MAX_NOME_AUDITORIA = 100;
/**
 * `<estoque> - Lote X` ou `<estoque> - Sem lote`, o `nome` gravado na auditoria.
 *
 * Nenhuma tela mostra o nome, mas a regra nega o Finalizar acima de 100, e o estoque (100)
 * mais o lote (40) passavam disso. O `slice` conta unidades UTF-16, como o `normalizarLote`;
 * o `replace` tira a metade alta de um emoji cortado no meio.
 */
export function nomeDaAuditoria(nomeEstoque, lote) {
    return `${nomeEstoque} - ${rotuloDaAuditoria({ lote })}`.slice(0, MAX_NOME_AUDITORIA).replace(/[\uD800-\uDBFF]$/, '');
}
const hora = (data) => data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
/**
 * Uma data por item, na mesma ordem: `DD/MM/AAAA`, mais ` HH:MM` quando outro item da lista
 * tem o mesmo lote aparado e a mesma data.
 *
 * O seletor de auditorias não mostra mais o `nome` gravado, e o site duplicava auditoria com
 * dois cliques até a 2.19: sem a hora, duas do mesmo lote e dia ficariam iguais. Os chips, a
 * tabela e o PDF do consolidado usam a mesma data, com o lote em outro lugar.
 */
export function datasDaLista(itens) {
    const chave = (i) => `${(i.lote ?? '').trim()}|${i.data.toLocaleDateString('pt-BR')}`;
    const vezes = new Map();
    for (const i of itens)
        vezes.set(chave(i), (vezes.get(chave(i)) ?? 0) + 1);
    return itens.map((i) => {
        const dia = i.data.toLocaleDateString('pt-BR');
        return (vezes.get(chave(i)) ?? 0) > 1 ? `${dia} ${hora(i.data)}` : dia;
    });
}
/** Um texto por auditoria, na mesma ordem: `Lote X — <data>` ou `<data>`, com a `<data>` do `datasDaLista`. */
export function rotulosDoSeletor(auditorias) {
    return datasDaLista(auditorias).map((data, n) => {
        const lote = (auditorias[n]?.lote ?? '').trim();
        return lote ? `Lote ${lote} — ${data}` : data;
    });
}
/** `Lote X · salva em <data e hora> · somente leitura` ou `Salva em <data e hora> · somente leitura`. */
export function subtituloDaAuditoriaSalva(auditoria) {
    const lote = (auditoria.lote ?? '').trim();
    const quando = `${auditoria.data.toLocaleString('pt-BR')} · somente leitura`;
    return lote ? `Lote ${lote} · salva em ${quando}` : `Salva em ${quando}`;
}
//# sourceMappingURL=lote.js.map